import { Plus } from "lucide-react";
import {
  CreateBase,
  Form,
  useDataProvider,
  useGetIdentity,
  useGetRecordRepresentation,
  useNotify,
  useRecordContext,
  useTranslate,
  useUpdate,
} from "ra-core";
import { useMemo, useState } from "react";
import { SaveButton } from "@/components/admin/form";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { TaskFormContent } from "./TaskFormContent";

export const AddTask = ({
  selectContact,
  display = "chip",
  dueDate,
  externalOpen,
  onExternalOpenChange,
  hideTrigger,
}: {
  selectContact?: boolean;
  display?: "chip" | "icon";
  /** Prefill the due date (ISO). Used by the calendar's click-a-day flow. */
  dueDate?: string;
  /** Controlled-open support so a parent (the calendar) can launch the dialog. */
  externalOpen?: boolean;
  onExternalOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
}) => {
  const { identity } = useGetIdentity();
  const dataProvider = useDataProvider();
  const [update] = useUpdate();
  const notify = useNotify();
  const translate = useTranslate();
  const contact = useRecordContext();
  const [open, setOpen] = useState(false);
  const isOpen = externalOpen ?? open;
  const closeDialog = () => {
    setOpen(false);
    onExternalOpenChange?.(false);
  };
  const handleOpen = () => {
    setOpen(true);
  };
  const getContactRepresentation = useGetRecordRepresentation("contacts");

  // Stable per opening - the same fix TaskCreateSheet already has. Built
  // inline, a fresh `new Date()` on every render made CreateBase see a "new"
  // record and reset the form, wiping whatever had been typed whenever
  // anything re-rendered after the user started (reliably on a slow machine:
  // found by CI's "user adding a task" e2e test).
  const record = useMemo(
    () => ({
      type: "none",
      contact_id: contact?.id,
      due_date: dueDate ?? new Date().toISOString(),
      sales_id: identity?.id,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isOpen, contact?.id, dueDate, identity?.id],
  );

  const handleSuccess = async (data: any) => {
    closeDialog();
    const contact = await dataProvider.getOne("contacts", {
      id: data.contact_id,
    });
    if (!contact.data) return;

    await update("contacts", {
      id: contact.data.id,
      data: { last_seen: new Date().toISOString() },
      previousData: contact.data,
    });

    notify("resources.tasks.added");
  };

  if (!identity) return null;

  return (
    <>
      {!hideTrigger && (display === "icon" ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                className="p-2 cursor-pointer"
                onClick={handleOpen}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {translate("resources.tasks.action.create")}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <div className="my-2">
          <Button
            variant="outline"
            className="h-6 cursor-pointer"
            onClick={handleOpen}
            size="sm"
          >
            <Plus className="w-4 h-4" />
            {translate("resources.tasks.action.add")}
          </Button>
        </div>
      ))}

      <CreateBase
        resource="tasks"
        record={record}
        mutationOptions={{ onSuccess: handleSuccess }}
      >
        <Dialog open={isOpen} onOpenChange={() => closeDialog()}>
          <DialogContent className="lg:max-w-xl overflow-y-auto max-h-9/10 top-1/20 translate-y-0">
            <Form className="flex flex-col gap-4">
              <DialogHeader>
                <DialogTitle>
                  {!selectContact
                    ? translate("resources.tasks.dialog.create_for", {
                        name: getContactRepresentation(contact!),
                      })
                    : translate("resources.tasks.dialog.create")}
                </DialogTitle>
              </DialogHeader>
              <TaskFormContent selectContact={selectContact} />
              <DialogFooter className="w-full justify-end">
                <SaveButton />
              </DialogFooter>
            </Form>
          </DialogContent>
        </Dialog>
      </CreateBase>
    </>
  );
};
